import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, AlertCircle, CheckCircle, Zap, ArrowLeft } from 'lucide-react';
import { normalizeApiError } from '../utils/normalizeApiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json();
        setError(normalizeApiError(data));
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      padding: 'var(--space-lg)'
    }} data-testid="forgot-password-page">
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: 'var(--glass-bg)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-2xl)',
        backdropFilter: 'blur(20px)'
      }}>
        {/* Logo / Brand */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto var(--space-lg)',
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Zap size={32} style={{ color: 'white' }} />
          </div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-xs)'
          }}>
            Reset Password
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {success 
              ? 'Check your email for a reset link'
              : "Enter your email and we'll send you a reset link"
            }
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: 'var(--space-md)',
            background: 'var(--status-danger-bg)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }} data-testid="forgot-password-error">
            <AlertCircle size={18} style={{ color: 'var(--status-danger)', flexShrink: 0 }} />
            <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>{error}</span>
          </div>
        )}

        {/* Success State */}
        {success ? (
          <div>
            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--status-success-bg)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-xl)',
              textAlign: 'center'
            }} data-testid="forgot-password-success">
              <CheckCircle size={48} style={{ color: 'var(--status-success)', marginBottom: 'var(--space-md)' }} />
              <p style={{ color: 'var(--status-success)', fontSize: '0.9375rem', fontWeight: '500' }}>
                Reset link sent!
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: 'var(--space-sm)' }}>
                If an account exists for {email}, you'll receive an email with instructions to reset your password.
              </p>
            </div>
            <Link
              to="/login"
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                fontSize: '1rem',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-sm)'
              }}
              data-testid="back-to-login-btn"
            >
              <ArrowLeft size={18} />
              Back to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Email Input */}
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-xs)',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{
                  position: 'absolute',
                  left: 'var(--space-md)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="forgot-password-email-input"
                  style={{
                    width: '100%',
                    padding: 'var(--space-md) var(--space-md) var(--space-md) calc(var(--space-md) * 2 + 18px)',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9375rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !email}
              className="btn btn-primary"
              data-testid="forgot-password-submit-btn"
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: 'var(--space-lg)'
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>

            {/* Back to Login */}
            <div style={{ textAlign: 'center' }}>
              <Link
                to="/login"
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}
                data-testid="back-to-login-link"
              >
                <ArrowLeft size={16} />
                Back to Login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
